import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { maskPhone } from "@/lib/utils-app";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Copy, Search, ArrowLeft, Wrench } from "lucide-react";

const DEFECTS = [
  { value: "Tela", label: "Tela" },
  { value: "Não carrega", label: "Não carrega" },
  { value: "Não liga", label: "Não liga" },
  { value: "Sem som", label: "Sem som" },
  { value: "Sem sinal", label: "Sem sinal" },
  { value: "Vidro quebrado", label: "Vidro quebrado" },
  { value: "Outros", label: "Outros" },
];

const maskCEP = (s = "") => String(s).replace(/\D/g, "").slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");

export default function RepairRequestPage() {
  const nav = useNavigate();
  const [f, setF] = useState({
    customer_name: "", customer_surname: "", customer_phone: "", customer_email: "",
    device_model: "", defect: "",
    cep: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "",
  });
  const [cepLoading, setCepLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null); // { os_code }

  const set = (k) => (e) => {
    let v = e.target.value;
    if (k === "customer_phone") v = maskPhone(v);
    if (k === "cep") v = maskCEP(v);
    setF((s) => ({ ...s, [k]: v }));
  };

  const lookupCep = async (cep) => {
    const digits = String(cep || "").replace(/\D/g, "");
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await r.json();
      if (data.erro) { toast.error("CEP não encontrado"); return; }
      setF((s) => ({
        ...s,
        street: data.logradouro || s.street,
        neighborhood: data.bairro || s.neighborhood,
        city: data.localidade || s.city,
        state: data.uf || s.state,
      }));
    } catch { toast.error("Falha ao buscar CEP"); }
    finally { setCepLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.defect) { toast.error("Selecione o defeito"); return; }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: f.customer_name.trim(),
        customer_surname: f.customer_surname.trim(),
        customer_phone: f.customer_phone.trim(),
        customer_email: f.customer_email.trim(),
        device_model: f.device_model.trim(),
        defect: f.defect,
        address: {
          cep: f.cep.trim(),
          street: f.street.trim(),
          number: f.number.trim(),
          complement: f.complement.trim(),
          neighborhood: f.neighborhood.trim(),
          city: f.city.trim(),
          state: f.state.trim(),
        },
      };
      const { data } = await api.post("/repair-requests", payload);
      setCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao enviar solicitação");
    } finally { setSubmitting(false); }
  };

  if (created) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Header />
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
          <div className="bg-white border border-neutral-200 rounded-3xl p-8 sm:p-12 text-center" data-testid="repair-success">
            <div className="w-16 h-16 bg-green-100 text-green-700 rounded-full grid place-items-center mx-auto">
              <CheckCircle2 size={32}/>
            </div>
            <div className="mt-6 text-xs uppercase tracking-[0.25em] font-bold text-neutral-500">Solicitação enviada</div>
            <div className="mt-2 font-mono text-5xl sm:text-6xl font-black tracking-tight text-neutral-900" data-testid="repair-os-code">{created.os_code}</div>
            <p className="mt-6 text-neutral-700 max-w-lg mx-auto leading-relaxed">
              Seu código OS foi enviado ao seu e-mail. Aguarde que um técnico aceite sua solicitação de reparo e entre em contato, você pode acompanhar o status da sua solicitação em{" "}
              <Link to={`/rastrear/${created.os_code}`} className="font-bold underline underline-offset-2" data-testid="repair-track-link">Verificar OS</Link>.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(created.os_code); toast.success("Código copiado!"); }} data-testid="repair-copy-code">
                <Copy size={14} className="mr-1.5"/> Copiar código
              </Button>
              <Button className="bg-neutral-900 hover:bg-neutral-800 text-white" onClick={() => nav(`/rastrear/${created.os_code}`)} data-testid="repair-goto-track">
                <Search size={14} className="mr-1.5"/> Verificar OS agora
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <Link to="/" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-neutral-500 hover:text-neutral-900" data-testid="repair-back">
          <ArrowLeft size={14}/> Voltar
        </Link>
        <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-neutral-500">
          <Wrench size={14}/> Nova Solicitação
        </div>
        <h1 className="mt-3 font-heading font-black text-3xl sm:text-5xl leading-[1.05] tracking-tight">Solicitar Reparo</h1>
        <p className="mt-4 text-neutral-600 max-w-xl">
          Preencha o formulário abaixo — geramos um código OS e o técnico entra em contato assim que aceitar sua solicitação.
        </p>

        <form onSubmit={submit} className="mt-10 grid gap-6" data-testid="repair-form">
          <fieldset className="bg-white border border-neutral-200 rounded-2xl p-6">
            <legend className="text-xs uppercase tracking-widest font-bold text-neutral-500 px-2">Contato</legend>
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              <div><Label>Nome</Label><Input value={f.customer_name} onChange={set("customer_name")} className="mt-1.5 h-11" data-testid="repair-name" required /></div>
              <div><Label>Sobrenome</Label><Input value={f.customer_surname} onChange={set("customer_surname")} className="mt-1.5 h-11" data-testid="repair-surname" required /></div>
              <div><Label>Número para contato</Label><Input value={f.customer_phone} onChange={set("customer_phone")} className="mt-1.5 h-11 font-mono" data-testid="repair-phone" required /></div>
              <div><Label>E-mail</Label><Input type="email" value={f.customer_email} onChange={set("customer_email")} className="mt-1.5 h-11" data-testid="repair-email" required /></div>
            </div>
          </fieldset>

          <fieldset className="bg-white border border-neutral-200 rounded-2xl p-6">
            <legend className="text-xs uppercase tracking-widest font-bold text-neutral-500 px-2">Aparelho</legend>
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              <div><Label>Qual o celular</Label><Input value={f.device_model} onChange={set("device_model")} placeholder="Ex.: iPhone 13 Pro, Galaxy S23" className="mt-1.5 h-11" data-testid="repair-device" required /></div>
              <div>
                <Label>Defeito</Label>
                <Select value={f.defect} onValueChange={(v) => setF((s) => ({ ...s, defect: v }))}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="repair-defect"><SelectValue placeholder="Selecione o defeito" /></SelectTrigger>
                  <SelectContent>
                    {DEFECTS.map((d) => (<SelectItem key={d.value} value={d.value} data-testid={`repair-defect-${d.value}`}>{d.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </fieldset>

          <fieldset className="bg-white border border-neutral-200 rounded-2xl p-6">
            <legend className="text-xs uppercase tracking-widest font-bold text-neutral-500 px-2">Endereço</legend>
            <div className="grid sm:grid-cols-6 gap-4 mt-2">
              <div className="sm:col-span-2">
                <Label>CEP</Label>
                <div className="relative">
                  <Input value={f.cep} onChange={set("cep")} onBlur={(e) => lookupCep(e.target.value)} placeholder="00000-000" className="mt-1.5 h-11 font-mono" data-testid="repair-cep" required />
                  {cepLoading && <Loader2 size={16} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500"/>}
                </div>
              </div>
              <div className="sm:col-span-4"><Label>Rua / Logradouro</Label><Input value={f.street} onChange={set("street")} className="mt-1.5 h-11" data-testid="repair-street" required /></div>
              <div className="sm:col-span-2"><Label>Número</Label><Input value={f.number} onChange={set("number")} className="mt-1.5 h-11" data-testid="repair-number" required /></div>
              <div className="sm:col-span-4"><Label>Complemento</Label><Input value={f.complement} onChange={set("complement")} placeholder="Apto, bloco, referência" className="mt-1.5 h-11" data-testid="repair-complement" /></div>
              <div className="sm:col-span-3"><Label>Bairro</Label><Input value={f.neighborhood} onChange={set("neighborhood")} className="mt-1.5 h-11" data-testid="repair-neighborhood" /></div>
              <div className="sm:col-span-2"><Label>Cidade</Label><Input value={f.city} onChange={set("city")} className="mt-1.5 h-11" data-testid="repair-city" /></div>
              <div className="sm:col-span-1"><Label>UF</Label><Input value={f.state} onChange={set("state")} maxLength={2} className="mt-1.5 h-11 uppercase" data-testid="repair-state" /></div>
            </div>
          </fieldset>

          <Button type="submit" disabled={submitting} className="h-14 rounded-md bg-neutral-900 hover:bg-neutral-800 text-white text-base font-semibold" data-testid="repair-submit">
            {submitting ? (<><Loader2 size={16} className="mr-2 animate-spin"/> Enviando solicitação de reparo…</>) : ("Confirmar")}
          </Button>
        </form>
      </section>

      {submitting && (
        <div className="fixed inset-0 z-50 bg-neutral-900/60 backdrop-blur grid place-items-center px-4" data-testid="repair-loading-overlay">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center">
            <Loader2 size={32} className="animate-spin mx-auto text-neutral-900"/>
            <div className="mt-5 font-heading font-bold text-lg">Enviando solicitação de reparo…</div>
            <div className="mt-1 text-sm text-neutral-500">Gerando sua OS. Isso leva alguns segundos.</div>
          </div>
        </div>
      )}
    </div>
  );
}
